import type {
  ApolloCache,
  DefaultContext,
  MutationResult,
  MutationUpdaterFunction,
} from '@apollo/client';
import { cloneDeep } from '@apollo/client/utilities';
import { useCallback } from 'react';

import type {
  AiOriginalImagesQuery,
  AiOriginalImagesQueryVariables,
  DeleteAiOriginalImageMutation,
  DeleteAiOriginalImageMutationVariables,
} from 'src/shared/graphql/generated';
import {
  AiOriginalImagesDocument,
  useDeleteAiOriginalImageMutation,
} from 'src/shared/graphql/generated';
import { logError } from 'src/shared/telemetry/logger';

const updateAiOriginalImagesCache: MutationUpdaterFunction<
  DeleteAiOriginalImageMutation,
  DeleteAiOriginalImageMutationVariables,
  DefaultContext,
  ApolloCache<any>
> = (cache, mutationResult) => {
  cache.updateQuery<AiOriginalImagesQuery, AiOriginalImagesQueryVariables>(
    { query: AiOriginalImagesDocument, variables: {} },
    prevData => {
      if (
        !prevData ||
        !prevData.aiOriginalImages ||
        !mutationResult.data?.deleteAiOriginalImage
      ) {
        return undefined;
      }

      const deletedImage = mutationResult.data.deleteAiOriginalImage;
      const existingResultIndex = prevData.aiOriginalImages.findIndex(
        result => result?.id === deletedImage.id,
      );

      if (existingResultIndex > -1) {
        // Remove the deleted aiOriginalImage from the AiOriginalImages cache
        const nextData = cloneDeep(prevData);
        nextData.aiOriginalImages?.splice(existingResultIndex, 1);
        return nextData;
      }

      return prevData;
    },
  );
};

export const useDeleteClickHandler = (
  id?: number,
): [() => void, MutationResult<DeleteAiOriginalImageMutation>] => {
  const [deleteAiOriginalImage, deleteAiOriginalImageResult] =
    useDeleteAiOriginalImageMutation();

  const deleteClickHandler = useCallback(() => {
    if (!id) {
      return;
    }

    void (async () => {
      const deleteResult = await deleteAiOriginalImage({
        variables: { id },
        update: updateAiOriginalImagesCache,
      });

      if (deleteResult.errors?.length) {
        deleteResult.errors.forEach(e => logError(e));
      }
    })();
  }, [deleteAiOriginalImage, id]);

  return [deleteClickHandler, deleteAiOriginalImageResult];
};
